import { ModalityGraphGroupConfig } from "../Types/ModalityGraphGroup"
import { ModalityTimeRow } from "../Atoms/ModalityTimes"
import { getVisibleGraphs } from "../Components/Visualizations/TimeSeriesGraphGroup/visibleGraphs"
import { getFullTraceDataConfigs, RenderStrategy } from "../Types/Trace"

const EEG_PREFIX = "EEG_"

export type ModalityJumpTarget = {
	label: string
	start: number
	end: number
}

const clampRange = (start: number, end: number, fileStart: number, fileEnd: number) => {
	const clampedStart = Math.max(start, fileStart)
	const clampedEnd = Math.min(end, fileEnd)

	if (clampedStart >= clampedEnd) {
		return undefined
	}

	return { start: clampedStart, end: clampedEnd }
}

export const buildModalityTargetsForTimeSeries = (
	config: ModalityGraphGroupConfig,
	rows: ModalityTimeRow[] | undefined,
	fileStart: number,
	fileEnd: number
): ModalityJumpTarget[] => {
	if (!rows || rows.length === 0) {
		return []
	}

	const visibleGraphs = getVisibleGraphs(config.graphs, config.patientModalities, config.emrData, config.hideEmptyGraphs)
	const visibleTraceLabels = new Map<string, string>()

	visibleGraphs
		.flatMap(graph => graph.traces)
		.filter(trace => trace.renderStrategy !== RenderStrategy.DELTA)
		.forEach(trace => {
			getFullTraceDataConfigs(trace).forEach(traceConfig => {
				if (
					!traceConfig.onDemandAnalysis &&
					(traceConfig.isVisible ?? true) &&
					!visibleTraceLabels.has(traceConfig.rawDataKey)
				) {
					visibleTraceLabels.set(
						traceConfig.rawDataKey,
						traceConfig.name || traceConfig.rawDataKey
					)
				}
			})
		})

	const resolvedTargets = Array.from(visibleTraceLabels.entries())
		.flatMap(([rawDataKey, name]) => {
			const row = rows.find(entry => entry.variable === rawDataKey)
			if (!row) {
				return []
			}

			const clamped = clampRange(row.min_start_time, row.max_end_time, fileStart, fileEnd)
			if (!clamped) {
				return []
			}

			return {
				rawDataKey,
				name,
				start: clamped.start,
				end: clamped.end,
			}
		})

	const duplicateNameCounts = resolvedTargets.reduce<Record<string, number>>((counts, target) => {
		counts[target.name] = (counts[target.name] ?? 0) + 1
		return counts
	}, {})

	return resolvedTargets
		.map(target => ({
			label: duplicateNameCounts[target.name] > 1
				? `${target.name} (${target.rawDataKey})`
				: target.name,
			start: target.start,
			end: target.end,
		}))
}

export const buildModalityTargetsForEEG = (
	rows: ModalityTimeRow[] | undefined,
	fileStart: number,
	fileEnd: number
): ModalityJumpTarget[] => {
	if (!rows || rows.length === 0) {
		return []
	}

	const eegRows = rows.filter(row => row.variable.startsWith(EEG_PREFIX))
	if (!eegRows.length) {
		return []
	}

	const start = Math.min(...eegRows.map(row => row.min_start_time))
	const end = Math.max(...eegRows.map(row => row.max_end_time))
	const clamped = clampRange(start, end, fileStart, fileEnd)

	if (!clamped) {
		return []
	}

	return [{ label: "EEG", start: clamped.start, end: clamped.end }]
}
